import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { SMS_STATUSES } from 'features/Home/constants/infoConstants'
import { PATIENT_STATUSES } from 'features/Home/constants/infoConstants'
import { useInfoManager } from 'features/Home/hooks/useInfoManager'
import { useFetchScheduleDayQuery } from 'features/Schedule/api/schedule.api'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import { sortCanceledSchedules } from 'features/Schedule/pages/components/utils'

dayjs.extend(customParseFormat)

export const useScheduleData = (date: string, selectedConsultationStaffId: string) => {
  const { siteId } = useParams()
  const { updateInfoDisabledIntervals } = useInfoManager(date)

  const { data: scheduleDay, isFetching: isFetchingDay } = useFetchScheduleDayQuery(
    { siteId, date },
    {
      skip: !date,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  const rooms = useMemo(() => {
    if (!scheduleDay) return []
    return scheduleDay.rooms
  }, [JSON.stringify(scheduleDay?.rooms)])

  const schedules = useMemo(() => {
    if (!scheduleDay) return []
    if (selectedConsultationStaffId === '-1') return sortCanceledSchedules(scheduleDay.items)
    const filteredItems = scheduleDay.items.filter(
      (item) => item.consultation_staff === Number(selectedConsultationStaffId),
    )
    return sortCanceledSchedules(filteredItems)
  }, [JSON.stringify(scheduleDay?.items), selectedConsultationStaffId])

  const bookingIds = useMemo(() => {
    if (!scheduleDay) return []
    const bookingIdSet = new Set<string>()

    rooms.forEach((room) => {
      if (room.booking_id) {
        bookingIdSet.add(String(room.booking_id))
      }
    })
    return Array.from(bookingIdSet)
  }, [JSON.stringify(rooms)])

  const consultationStaffs = useMemo(() => {
    if (!scheduleDay) return []
    return scheduleDay.consultation_staffs
  }, [JSON.stringify(scheduleDay?.consultation_staffs)])

  const languages = useMemo(() => {
    if (!scheduleDay) return []
    return scheduleDay.languages
  }, [JSON.stringify(scheduleDay?.languages)])

  const timeInterval = useMemo(() => {
    if (!scheduleDay || scheduleDay?.time_interval?.min === null) return { min: null, max: null }
    return {
      min: dayjs(scheduleDay.time_interval.min, 'HH:mm:ss').format('HH:mm'),
      max: dayjs(scheduleDay.time_interval.max, 'HH:mm:ss').format('HH:mm'),
    }
  }, [JSON.stringify(scheduleDay?.time_interval)])

  const defaultAppointmentTime = useMemo(() => {
    if (!scheduleDay) return 0
    return scheduleDay.time_interval.default_appointment_time
  }, [JSON.stringify(scheduleDay?.time_interval)])

  const disabledIntervals = useMemo(() => {
    if (!scheduleDay) return []
    return updateInfoDisabledIntervals(scheduleDay.rooms, scheduleDay.time_interval)
  }, [JSON.stringify(scheduleDay?.rooms), JSON.stringify(scheduleDay?.time_interval)])

  const eventsToSendNotification = useMemo(() => {
    if (!scheduleDay) return []
    return schedules
      .filter(
        (item) =>
          item.sms_notification === SMS_STATUSES.UNCONFIRMED &&
          item.status.code === PATIENT_STATUSES.UNCONFIRMED,
      )
      .map((item) => item.id)
  }, [JSON.stringify(schedules)])

  const roomStatuses = useMemo(() => {
    if (!scheduleDay) return []
    return scheduleDay.room_statuses
  }, [JSON.stringify(scheduleDay?.room_statuses)])

  const patientStatuses = useMemo(() => {
    if (!scheduleDay) return []
    return scheduleDay.patient_statuses
  }, [JSON.stringify(scheduleDay?.patient_statuses)])

  const doctorBufferTime = useMemo(() => {
    if (!scheduleDay) return { before: null, after: null }
    return scheduleDay.doctor_buffer_time
  }, [JSON.stringify(scheduleDay?.doctor_buffer_time)])

  const appointmentCategories = useMemo(() => {
    if (!scheduleDay) return []
    return scheduleDay.appointment_categories
  }, [JSON.stringify(scheduleDay?.appointment_categories)])

  const hasEhrBooking = useMemo(() => {
    return !!scheduleDay?.has_ehr_booking
  }, [JSON.stringify(scheduleDay?.has_ehr_booking)])

  const hasOccupied = useMemo(() => {
    return !!scheduleDay?.has_occupied
  }, [JSON.stringify(scheduleDay?.has_occupied)])

  const byPassTemperatureCheck = useMemo(() => {
    return scheduleDay?.pandemic_protocol === 0
  }, [JSON.stringify(scheduleDay?.pandemic_protocol)])

  const appointmentColor = useMemo(() => {
    if (!scheduleDay) return 'status'
    return scheduleDay.appointment_color
  }, [JSON.stringify(scheduleDay?.appointment_color)])

  const punctuality = useMemo(() => {
    if (!scheduleDay) return []
    return scheduleDay.punctuality
  }, [JSON.stringify(scheduleDay?.punctuality)])

  const patientBufferTime = useMemo(() => {
    if (!scheduleDay) return { after: 0, before: 0 }
    return scheduleDay.patient_buffer_time
  }, [JSON.stringify(scheduleDay?.patient_buffer_time)])

  return {
    bookingIds,
    scheduleDay,
    rooms,
    schedules,
    consultationStaffs,
    languages,
    timeInterval,
    disabledIntervals,
    isFetchingDay,
    eventsToSendNotification,
    roomStatuses,
    doctorBufferTime,
    defaultAppointmentTime,
    patientStatuses,
    appointmentCategories,
    hasEhrBooking,
    hasOccupied,
    byPassTemperatureCheck,
    appointmentColor,
    punctuality,
    patientBufferTime,
  }
}
